import { ChartTooltip, ChartValueAxis, ChartValueAxisItem, Sparkline } from "@progress/kendo-react-charts";
import { daysUntilReset } from "../../helper";

export interface UsageSparklineProps {
  used:       number;
  limit:      number;
  color?:     string;
  label?:     string;
  resetDate?: Date;
}

export function UsageSparkline({ used, limit, resetDate }: UsageSparklineProps) {
  const barColor   = usageColor(used, limit);
  const labelColor = usageLabelColor(used, limit);

  return (
    <div className="d-flex align-items-center gap-2">
      <Sparkline
        type="bar"
        data={[[used, limit]]}
        seriesColors={[barColor]}
      >
        <ChartValueAxis>
          <ChartValueAxisItem min={0} max={limit} />
        </ChartValueAxis>
        <ChartTooltip
          shared={true}
          background="#fff"
          border={{ color: "#e5e7eb", width: 1 }}
          render={() => renderSparklineTooltip(used, limit, barColor, resetDate)}
        />
      </Sparkline>

      <span className="small fw-semibold text-nowrap" style={{ color: labelColor }}>
        {used}/{limit}
      </span>
    </div>
  );
}

export function usageLabelColor(used: number, limit: number): string {
  if (limit === 0) return '#6b7280';
  const pct = used / limit;
  if (used > limit)  return '#c45e00';   
  if (pct >= 0.9)    return '#b45309';  
  if (pct >= 0.7)    return '#1d4ed8';   
  return '#1a9a6b';                       
}

export function usageColor(used: number, limit: number): string {
  if (limit === 0) return '#9ca3af';
  const pct = used / limit;
  if (used > limit)  return '#FF7F11';  
  if (pct >= 0.9)    return '#f59e0b';   
  if (pct >= 0.7)    return '#60a5fa';   
  return '#1a9a6b';                       
}

const renderSparklineTooltip = (used: number, limit: number, barColor: string, resetDate?: Date) => {
  const pct       = limit > 0 ? Math.round((used / limit) * 100) : 0;
  const over      = used > limit;
  const remaining = Math.max(0, limit - used);
  const daysLeft  = daysUntilReset(resetDate);
  const pctColor  = over ? '#c45e00' : pct >= 90 ? '#b45309' : pct >= 70 ? '#1d4ed8' : '#065f46';
  const pillBg    = over ? '#fff0e0' : pct >= 70 ? '#fef3c7' : '#d1fae5';

  return (
    <div className="p-2" style={{ minWidth: 160, fontSize: '12px' }}>

      {/* Big number */}
      <div className="d-flex align-items-baseline gap-1 mb-2">
        <span className="fw-bold" style={{ fontSize: '22px' }}>{used}</span>
        <span className="text-secondary">/ {limit}</span>
      </div>

      {/* Progress bar */}
      <div className="mb-2 rounded" style={{ height: 4, background: '#e5e7eb', overflow: 'hidden' }}>
        <div className="rounded" style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: barColor }} />
      </div>

      {/* Stats rows */}
      <div className="d-flex justify-content-between mb-1">
        <span className="text-secondary">Used</span>
        <span className="fw-semibold">{used}</span>
      </div>
      <div className="d-flex justify-content-between mb-1">
        <span className="text-secondary">{over ? 'Topped Off' : 'Remaining'}</span>
        <span className="fw-semibold" style={{ color: over ? '#FF7F11' : undefined }}>
          {over ? used - limit : remaining}
        </span>
      </div>
      <div className="d-flex justify-content-between pt-2 mt-1" style={{ borderTop: '1px solid #e5e7eb' }}>
        <span className="text-secondary">Monthly plan</span>
        <span className="fw-semibold">{limit}</span>
      </div>

      {/* Reset row */}
      {daysLeft !== null && (
        <div className="d-flex justify-content-between pt-2 mt-1" style={{ borderTop: '1px solid #e5e7eb' }}>
          <span className="text-secondary">Resets in</span>
          <span className="fw-semibold" style={{ color: daysLeft <= 3 ? '#f59e0b' : undefined }}>
            {daysLeft === 0 ? 'Today' : `${daysLeft}d`}
          </span>
        </div>
      )}

      {/* Status pill */}
      <div className="text-center mt-2 px-2 py-1 rounded" style={{ background: pillBg }}>
        <span className="fw-bold" style={{ color: pctColor, fontSize: '11px' }}>
          {over ? ` Adding Onto Plan` : `${pct}% of monthly plan`}
        </span>
      </div>

    </div>
  );
};
